import 'swiper/css';

import { IconChevronLeft } from '@actr/icons/chevron-left';
import { IconChevronRight } from '@actr/icons/chevron-right';
import { IBaseComponentProps } from '@actr/specs/src/base-component-props';
import { Sizes } from '@actr/style/src/specs/theme-v2';
import { Button, ButtonSizes, ButtonTypes } from '@actr/ui/src/components/_atoms/button';
import { FlexDirection, FlexJustify, FlexWrapper } from '@actr/ui/src/components/_atoms/flex-wrapper';
import { Space } from '@actr/ui/src/components/_atoms/space';
import { Text } from '@actr/ui/src/components/_atoms/text';
import { ContentBlock } from '@actr/ui/src/components/content-block';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import React, { FC, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper/types';

import { VisaFreeDestinations } from '@/services/models/avia/main-page-info';

import DestinationCard from './destination-card';
import * as Styled from './style.css';

export interface DestinationsBlockProps extends IBaseComponentProps {
  data: VisaFreeDestinations;
}

const DestinationsBlock: FC<DestinationsBlockProps> = observer(({ data, className }) => {
  const refSwiper = useRef<SwiperType | undefined>(undefined);
  const { title, description, list } = data;
  // Swiper 12 требует больше слайдов, чем одновременно видно, для стабильного loop.
  const canLoop = list.length > 3;

  return (
    <ContentBlock>
      <FlexWrapper direction={FlexDirection.ROW} gap={Sizes.X2S} justify={FlexJustify.SPACE_BETWEEN}>
        <FlexWrapper direction={FlexDirection.COL} gap={Sizes.X4S}>
          <Text tagName="h3" font="title.x2l">{title}</Text>
          {!!description && (
            <Text font="paragraph.m" className={Styled.Description}>
              {description}
            </Text>
          )}
        </FlexWrapper>
        {canLoop && (
          <FlexWrapper direction={FlexDirection.ROW} gap={Sizes.X2S} className={Styled.SliderButtons}>
            <Button
              size={ButtonSizes.S}
              buttonType={ButtonTypes.TERTIARY}
              leftIcons={(<IconChevronLeft />)}
              className={Styled.SliderButton}
              onClick={() => refSwiper.current?.slidePrev()}
            />
            <Button
              size={ButtonSizes.S}
              buttonType={ButtonTypes.TERTIARY}
              leftIcons={(<IconChevronRight />)}
              className={Styled.SliderButton}
              onClick={() => refSwiper.current?.slideNext()}
            />
          </FlexWrapper>
        )}
      </FlexWrapper>
      <Space size={Sizes.M} />
      {/* CSS-переключение сохраняет одинаковую SSR-разметку и не показывает desktop-Swiper на мобильном до гидрации. */}
      <div className={Styled.MobileWrapper}>
        <div className={Styled.MobileBar}>
          {list.map((item, index) => (
            <DestinationCard
              name={item.name}
              description={item.description}
              info={item.info}
              image={item.image}
              route={item.route}
              imageIndex={index}
              showSchema={false}
              key={index}
              fixedImgWidth="216px"
              minPrice={item.min_price}
            />
          ))}
        </div>
      </div>
      <FlexWrapper className={classNames(Styled.DestinationsCardsWrapper, className)} gap={Sizes.M}>
        <Swiper
          slidesPerView={3}
          spaceBetween={16}
          loop={canLoop}
          roundLengths
          onBeforeInit={swiper => {
            refSwiper.current = swiper;
          }}>
          {list.map((item, index) => (
            <SwiperSlide key={index} className={Styled.Slide}>
              <DestinationCard
                name={item.name}
                description={item.description}
                info={item.info}
                image={item.image}
                route={item.route}
                imageIndex={index}
                showSchema={false}
                fixedImgWidth="216px"
                minPrice={item.min_price}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </FlexWrapper>
    </ContentBlock>
  );
});

export default DestinationsBlock;
